// Single source of truth for every lazily-loaded admin tab's dynamic import.
//
// It exists so two otherwise-unrelated components can share the same
// import() call for each tab:
//   - SuperAdminDashboardPage.js uses the lazy()-wrapped component to render
//     whichever tab is active.
//   - AdminDashboardLayout.js (the sidebar) calls the matching preload
//     function on hover, so the tab's code is already downloading by the
//     time the click actually lands — a real dynamic import() call is
//     idempotent and browser-cached, so warming it here and having
//     React.lazy() request the exact same module later just resolves it
//     from cache instead of kicking off a second fetch.
//
// A named export needs the `.then(m => ({ default: m.X }))` unwrap since
// React.lazy only accepts a module with a default export; importing straight
// from each tab's own file (not the AdminTabs.js barrel) matters too — a
// dynamic import of the barrel would pull in every tab to get one, since the
// barrel's only job is re-exporting all of them.
import { lazy } from 'react';

const tabImport = {
    AnalyticsTab: () => import('./tabs/AnalyticsTab'),
    PermissionsTab: () => import('./tabs/PermissionsTab'),
    RolesTab: () => import('./tabs/RolesTab'),
    SettingsTab: () => import('./tabs/SettingsTab'),
    DatabaseTab: () => import('./tabs/DatabaseTab'),
    ActivityMonitorTab: () => import('./tabs/ActivityMonitorTab'),
    ReportsTab: () => import('./tabs/ReportsTab'),
    SupportTab: () => import('./tabs/SupportTab'),
    ApiManagementTab: () => import('./tabs/ApiManagementTab'),
    PaymentGatewayTab: () => import('./tabs/PaymentGatewayTab'),
    KycManagementTab: () => import('./tabs/KycManagementTab'),
    LoansTab: () => import('./tabs/LoansTab'),
    LeadsTab: () => import('./tabs/LeadsTab'),
    SalesLeadsTab: () => import('./tabs/SalesLeadsTab'),
    DataRecordTab: () => import('./tabs/DataRecordTab'),
    BreDecisionTab: () => import('./tabs/BreDecisionTab'),
    GlobalSearchTab: () => import('./tabs/GlobalSearchTab'),
    InvestorDashboardTab: () => import('./tabs/InvestorDashboardTab'),
    CollectionReportTab: () => import('./tabs/CollectionReportTab'),
    MyDashboardTab: () => import('./tabs/MyDashboardTab'),
    StaffTargetsTab: () => import('./tabs/StaffTargetsTab'),
    WhatsAppMarketingTab: () => import('./tabs/WhatsAppMarketingTab'),
    BankAndTransactionsTab: () => import('./BankAndTransactionsTab')
};

export const AnalyticsTab = lazy(() => tabImport.AnalyticsTab().then(m => ({ default: m.AnalyticsTab })));
export const PermissionsTab = lazy(() => tabImport.PermissionsTab().then(m => ({ default: m.PermissionsTab })));
export const RolesTab = lazy(() => tabImport.RolesTab().then(m => ({ default: m.RolesTab })));
export const SettingsTab = lazy(() => tabImport.SettingsTab().then(m => ({ default: m.SettingsTab })));
export const DatabaseTab = lazy(() => tabImport.DatabaseTab().then(m => ({ default: m.DatabaseTab })));
export const ActivityMonitorTab = lazy(() => tabImport.ActivityMonitorTab().then(m => ({ default: m.ActivityMonitorTab })));
export const ReportsTab = lazy(() => tabImport.ReportsTab().then(m => ({ default: m.ReportsTab })));
export const SupportTab = lazy(() => tabImport.SupportTab().then(m => ({ default: m.SupportTab })));
export const ApiManagementTab = lazy(() => tabImport.ApiManagementTab().then(m => ({ default: m.ApiManagementTab })));
export const PaymentGatewayTab = lazy(() => tabImport.PaymentGatewayTab().then(m => ({ default: m.PaymentGatewayTab })));
export const KycManagementTab = lazy(() => tabImport.KycManagementTab().then(m => ({ default: m.KycManagementTab })));
export const LoansTab = lazy(() => tabImport.LoansTab().then(m => ({ default: m.LoansTab })));
export const LeadsTab = lazy(() => tabImport.LeadsTab().then(m => ({ default: m.LeadsTab })));
export const SalesLeadsTab = lazy(() => tabImport.SalesLeadsTab().then(m => ({ default: m.SalesLeadsTab })));
export const DataRecordTab = lazy(() => tabImport.DataRecordTab().then(m => ({ default: m.DataRecordTab })));
export const BreDecisionTab = lazy(() => tabImport.BreDecisionTab().then(m => ({ default: m.BreDecisionTab })));
export const GlobalSearchTab = lazy(() => tabImport.GlobalSearchTab().then(m => ({ default: m.GlobalSearchTab })));
export const InvestorDashboardTab = lazy(() => tabImport.InvestorDashboardTab().then(m => ({ default: m.InvestorDashboardTab })));
export const CollectionReportTab = lazy(() => tabImport.CollectionReportTab().then(m => ({ default: m.CollectionReportTab })));
export const MyDashboardTab = lazy(() => tabImport.MyDashboardTab().then(m => ({ default: m.MyDashboardTab })));
export const StaffTargetsTab = lazy(() => tabImport.StaffTargetsTab().then(m => ({ default: m.StaffTargetsTab })));
export const WhatsAppMarketingTab = lazy(() => tabImport.WhatsAppMarketingTab().then(m => ({ default: m.WhatsAppMarketingTab })));
export const BankAndTransactionsTab = lazy(() => tabImport.BankAndTransactionsTab().then(m => ({ default: m.BankAndTransactionsTab })));

// Maps every `?tab=` key the sidebar can navigate to onto the component that
// key renders — several keys (the loan queues) share one component, so
// hovering any of them just warms the same LoansTab chunk.
const TAB_KEY_TO_COMPONENT = {
    globalSearch: 'GlobalSearchTab',
    investorDashboard: 'InvestorDashboardTab',
    collectionReport: 'CollectionReportTab',
    analytics: 'AnalyticsTab',
    permissions: 'PermissionsTab',
    roles: 'RolesTab',
    settings: 'SettingsTab',
    database: 'DatabaseTab',
    sanctionLetters: 'DataRecordTab',
    crifData: 'DataRecordTab',
    crifJson: 'DataRecordTab',
    crifHtml: 'DataRecordTab',
    aadhaarInfo: 'DataRecordTab',
    bankStatements: 'DataRecordTab',
    breDecision: 'BreDecisionTab',
    activity: 'ActivityMonitorTab',
    reports: 'ReportsTab',
    support: 'SupportTab',
    api: 'ApiManagementTab',
    payments: 'PaymentGatewayTab',
    kyc: 'KycManagementTab',
    loans: 'LoansTab',
    'queue-credit-exec': 'LoansTab',
    'queue-credit-head': 'LoansTab',
    'queue-deep-review': 'LoansTab',
    'queue-esign': 'LoansTab',
    'queue-disbursal': 'LoansTab',
    'queue-collection': 'LoansTab',
    'queue-closed': 'LoansTab',
    bankAndTransactions: 'BankAndTransactionsTab',
    leads: 'LeadsTab',
    salesLeads: 'SalesLeadsTab',
    whatsappMarketing: 'WhatsAppMarketingTab',
    myDashboard: 'MyDashboardTab',
    staffTargets: 'StaffTargetsTab'
};

const preloaded = new Set();

// Warms the browser's module cache for a tab's chunk before it's actually
// opened. Safe to call repeatedly (a hover that fires many times, moving the
// mouse across a link) — only issues the real import() once per tab per page
// load, same as import() itself would dedupe, but this also skips it for
// the current tab and out-of-scope keys instead of doing a no-op fetch.
export function preloadTabChunk(tabKey) {
    const componentName = TAB_KEY_TO_COMPONENT[tabKey];
    if (!componentName || preloaded.has(componentName)) return;
    preloaded.add(componentName);
    tabImport[componentName]().catch(() => {
        // A failed prefetch (offline, flaky connection) isn't an error worth
        // surfacing — the normal lazy() import on actual click will just
        // retry and show its usual loading/error state.
        preloaded.delete(componentName);
    });
}
